// src/utils/ayanamsa.ts
export type AyanamsaProvider = (date: Date) => number;

export const norm360 = (x: number) => (x % 360 + 360) % 360;

export const constantAyanamsa = (valueDeg = 24.0): AyanamsaProvider =>
  (_date: Date) => valueDeg;

export function julianDay(date: Date): number {
  return date.getTime() / 86400000 + 2440587.5;
}

function yearsSinceJ2000(date: Date): number {
  const JD = julianDay(date);
  return (JD - 2451545.0) / 365.2422;
}

/** === By-date approximate providers (good to a few arc-min, fine for UI) ===
 * All use the same linear precession rate (~50.3″/yr) with different J2000 anchors.
 * Tweak anchors if you want to match a specific ephemeris exactly.
 */

// Lahiri (Chitra)
export const lahiriAyanamsaApprox: AyanamsaProvider = (date: Date) => {
  const LAHIRI_AT_J2000 = 23.85308;   // deg
  const RATE_DEG_PER_YEAR = 0.013969; // deg/year
  const years = yearsSinceJ2000(date);
  return norm360(LAHIRI_AT_J2000 + RATE_DEG_PER_YEAR * years);
};

// Raman (approx)
export const ramanAyanamsaApprox: AyanamsaProvider = (date: Date) => {
  const RAMAN_AT_J2000 = 22.506;      // ≈22°30′ (adjust if you prefer)
  const RATE_DEG_PER_YEAR = 0.013969;
  const years = yearsSinceJ2000(date);
  return norm360(RAMAN_AT_J2000 + RATE_DEG_PER_YEAR * years);
};

// Fagan/Bradley (approx)
export const faganBradleyAyanamsaApprox: AyanamsaProvider = (date: Date) => {
  const FB_AT_J2000 = 24.042;         // ≈24°02′ (adjust if you prefer)
  const RATE_DEG_PER_YEAR = 0.013969;
  const years = yearsSinceJ2000(date);
  return norm360(FB_AT_J2000 + RATE_DEG_PER_YEAR * years);
};

/** Selector */
export type AyanamsaKey = 'lahiri' | 'raman' | 'fagan_bradley' | 'custom';

export function getAyanamsaProvider(key: AyanamsaKey, customDeg?: number): AyanamsaProvider {
  switch (key) {
    case 'lahiri':         return lahiriAyanamsaApprox;
    case 'raman':          return ramanAyanamsaApprox;
    case 'fagan_bradley':  return faganBradleyAyanamsaApprox;
    case 'custom':         return constantAyanamsa(customDeg ?? 24.0);
    default:               return lahiriAyanamsaApprox;
  }
}
